import React from "react";
import { Calendar, MessageCircle, CheckSquare, Square, AlertCircle, Tag } from "lucide-react";
import Avatar, { AvatarStack } from "./Avatar";
import { useWorkspace } from "../contexts/WorkspaceContext";

const PRIORITY = {
  urgent: { color: "text-[#EF4444]", bg: "bg-[#EF4444]/10", border: "border-[#EF4444]/30", label: "URGENT" },
  high: { color: "text-[#F59E0B]", bg: "bg-[#F59E0B]/10", border: "border-[#F59E0B]/30", label: "HIGH" },
  medium: { color: "text-[#38BDF8]", bg: "bg-[#38BDF8]/10", border: "border-[#38BDF8]/30", label: "MED" },
  low: { color: "text-zinc-500", bg: "bg-zinc-500/10", border: "border-zinc-500/30", label: "LOW" },
};

function overdueText(due) {
  if (!due) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDate = new Date(due);
  const diffMs = dueDate.getTime() - today.getTime();
  const days = Math.round(diffMs / (1000 * 60 * 60 * 24));
  if (days < 0) {
    const abs = Math.abs(days);
    if (abs >= 365) return `Overdue by ${Math.floor(abs / 365)} year${Math.floor(abs / 365) > 1 ? "s" : ""}`;
    if (abs >= 30) return `Overdue by ${Math.floor(abs / 30)} month${Math.floor(abs / 30) > 1 ? "s" : ""}`;
    return `Overdue by ${abs} day${abs > 1 ? "s" : ""}`;
  }
  if (days === 0) return "Due today";
  if (days <= 7) return `Due in ${days}d`;
  return null;
}

export default function TaskCard({ task, onClick, onToggle, draggable = true, onDragStart, onDragEnd, isDragging }) {
  const { members } = useWorkspace();
  const assignedUsers = (task.assignees || []).map((id) => members.find((m) => m.id === id)).filter(Boolean);
  const overdue = overdueText(task.due_date);
  const priority = PRIORITY[task.priority] || PRIORITY.medium;
  const checklistDone = (task.checklist || []).filter((c) => c.done).length;
  const checklistTotal = (task.checklist || []).length;
  const isDone = task.status === "done";

  return (
    <div
      onClick={onClick}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      data-testid={`task-card-${task.id}`}
      className={`group bg-[#141417] border border-zinc-800 hover:border-zinc-600 rounded-lg p-3 cursor-pointer task-card-shadow transition-colors ${
        isDragging ? "dragging" : ""
      } ${isDone ? "opacity-60" : ""}`}
    >
      <div className="flex items-start gap-2">
        <button
          onClick={(e) => { e.stopPropagation(); onToggle && onToggle(task); }}
          data-testid={`task-toggle-${task.id}`}
          className={`mt-0.5 shrink-0 transition-colors ${
            isDone ? "text-[#10B981]" : "text-zinc-600 hover:text-[#D4A968]"
          }`}
          title={isDone ? "Mark open" : "Mark done"}
        >
          {isDone ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className={`text-sm leading-snug ${isDone ? "line-through text-zinc-500" : "text-white"}`}>
            {task.title}
          </div>
          {overdue && (
            <div
              className={`text-[11px] mt-1 font-mono ${
                overdue.startsWith("Overdue") ? "text-[#EF4444] pulse-soft" : "text-[#F59E0B]"
              }`}
              data-testid={`task-overdue-${task.id}`}
            >
              {overdue}
            </div>
          )}
        </div>
      </div>

      {task.tags && task.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {task.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="text-[9px] uppercase font-mono tracking-wider text-zinc-400 bg-[#1B1B1F] border border-zinc-800 px-1.5 py-0.5 rounded">
              {tag}
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between mt-2.5">
        <div className="flex items-center gap-2">
          {assignedUsers.length > 0 ? (
            <AvatarStack users={assignedUsers} size="xs" max={3} />
          ) : (
            <div className="w-5 h-5 rounded-full border border-dashed border-zinc-700"></div>
          )}
        </div>
        <div className="flex items-center gap-1.5 text-[10px] font-mono text-zinc-500">
          {checklistTotal > 0 && (
            <span className="flex items-center gap-0.5">
              <CheckSquare className="w-3 h-3" />
              {checklistDone}/{checklistTotal}
            </span>
          )}
          <span className={`px-1.5 py-0.5 border ${priority.bg} ${priority.border} ${priority.color} rounded`}>
            {priority.label}
          </span>
        </div>
      </div>
    </div>
  );
}
